/* eslint-disable no-script-url */
import React, { useContext, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { CtaButton } from './CTASection.styles';
import { vehicleContext } from '../VehicleProvider';
import { MARKET } from '../../constants';
import { OaddEligibleDealer } from '../../hooks/useGetOaddDealers';

interface FirstButtonProps {
  weblink: string;
  isSticky?: boolean;
  firstButtonType: string;
  bookATestDriveUrl?: string;
  requestAQuoteUrl?: string;
  howEDepositWorksUrl?: string;
  paymentCalculatorUrl?: string;
  eligibleOaddDealers: OaddEligibleDealer[];
  eligibleDealerIds: string[];
}

const FirstButton: React.FC<FirstButtonProps> = ({
  weblink,
  isSticky,
  firstButtonType,
  bookATestDriveUrl,
  requestAQuoteUrl,
  howEDepositWorksUrl,
  paymentCalculatorUrl,
  eligibleOaddDealers,
  eligibleDealerIds,
}) => {
  const { t } = useTranslation();

  const isMarketUSA = MARKET === 'USA';

  // @ts-ignore
  const windowVisitor = typeof window !== 'undefined' ? window?.ss?.visitor : '';
  const mcvid = windowVisitor?.getMarketingCloudVisitorID();
  const mcvidParam = mcvid ? `mcvid=${mcvid}` : '';

  const weblinkWithDDC = weblink?.includes('?')
    ? weblink?.concat(`&ddcref=tier1_inventorysearch&${mcvidParam}`)
    : weblink?.concat(`?ddcref=tier1_inventorysearch&${mcvidParam}`);

  const { vehicle, market } = useContext(vehicleContext);

  const correctDealerListingUrl = useMemo(() => {
    if (!vehicle) return weblinkWithDDC;
    if (eligibleDealerIds.includes(vehicle?.kvpsSyncId)) {
      return eligibleOaddDealers.find((dealer) => dealer.dealerId === vehicle?.kvpsSyncId)?.rootUrl;
    }
    return weblinkWithDDC;
  }, [eligibleDealerIds, eligibleOaddDealers, vehicle, weblinkWithDDC]);

  const digitalLeadsData = JSON.stringify({
    vehicleType: vehicle?.vehicleType,
    market,
    presetForm: 'contact-dealer',
    dealerId: vehicle?.kvpsSyncId,
  });

  // CTA FUNCTIONAL SUMMARY for US and CAN market in README.md

  // BOOK_A_TEST_DRIVE button
  const bookATestDriveButton = (
    <CtaButton
      type="secondary"
      href={bookATestDriveUrl}
      sticky={isSticky}
      data-testid="book-test-drive-button"
    >
      {t('vehiclePricingBar.dealerListPrice.bookATestDrive')}
    </CtaButton>
  );

  // REQUEST_A_QUOTE button
  const requestAQuoteButton = (
    <CtaButton
      type="secondary"
      href={isMarketUSA ? 'javascript:void(0)' : requestAQuoteUrl}
      sticky={isSticky}
      data-testid="request-quote-button"
      id="digital-leads"
      data-info={digitalLeadsData}
    >
      {t('vehiclePricingBar.dealerListPrice.contactDealer')}
    </CtaButton>
  );

  // HOW_E_DEPOSIT_WORKS button
  const howEDepositWorksButton = (
    <CtaButton
      type="secondary"
      href={howEDepositWorksUrl}
      sticky={isSticky}
      data-testid="how-e-deposit-works-button"
    >
      {t('vehiclePricingBar.dealerListPrice.howEDepositWorks')}
    </CtaButton>
  );

  // VISIT_DEALER_LISTING button
  const visitDealerListingButton = (
    <CtaButton
      type="secondary"
      href={correctDealerListingUrl}
      sticky={isSticky}
      data-testid="visit-dealer-website-button"
    >
      {t('vehiclePricingBar.dealerListPrice.visitDealerListing')}
    </CtaButton>
  );

  // START_ONLINE_SHOPPING button
  const startOnlineShoppingButton = (
    <CtaButton
      type="secondary"
      sticky={isSticky}
      href={paymentCalculatorUrl}
      data-testid="start-payment-calculation-button"
    >
      {t('vehiclePricingBar.dealerListPrice.startOnlineShopping')}
    </CtaButton>
  );

  if (firstButtonType === 'BOOK_A_TEST_DRIVE') return bookATestDriveButton;

  if (firstButtonType === 'REQUEST_A_QUOTE') return requestAQuoteButton;

  if (firstButtonType === 'HOW_E_DEPOSIT_WORKS') return howEDepositWorksButton;

  if (firstButtonType === 'VISIT_DEALER_LISTING') return visitDealerListingButton;

  if (firstButtonType === 'START_ONLINE_SHOPPING') return startOnlineShoppingButton;

  return null;
};

export default FirstButton;
